import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiDelete, apiGet, apiPost } from '@/lib/http/client';

import type { WhatsAppLinkResponse, WhatsAppStatus } from './types';

export const whatsappStatusQueryKey = (orgId: number | string) =>
  ['whatsappStatus', orgId] as const;

export function useWhatsAppStatus(
  orgId: number | string,
  opts?: { pollForVerification?: boolean }
) {
  return useQuery<WhatsAppStatus>({
    queryKey: whatsappStatusQueryKey(orgId),
    queryFn: () =>
      apiGet<WhatsAppStatus>(`/api/orgs/${orgId}/whatsapp/status/`),
    // Don't fire against a placeholder org id (0) while org state is still
    // loading — it would produce an avoidable failed /api/orgs/0/whatsapp/status/ call.
    enabled: Boolean(orgId),
    // Polling is only active in the window between "code issued" and "linked
    // confirmed" — it self-stops the instant either resolves (the caller
    // passes pollForVerification: false once linked becomes true or no code
    // has been issued). 5000ms matches the active-polling interval used by
    // useStripeConnections/useMailchimpStatus.
    refetchInterval: opts?.pollForVerification ? 5000 : false,
  });
}

export function useIssueWhatsAppLink(orgId: number | string) {
  const qc = useQueryClient();
  return useMutation<WhatsAppLinkResponse, unknown>({
    mutationFn: () =>
      apiPost<WhatsAppLinkResponse>(`/api/orgs/${orgId}/whatsapp/link/`, {}),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: whatsappStatusQueryKey(orgId) }),
  });
}

export function useUnlinkWhatsApp(orgId: number | string) {
  const qc = useQueryClient();
  return useMutation<unknown, unknown>({
    mutationFn: () => apiDelete<unknown>(`/api/orgs/${orgId}/whatsapp/link/`),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: whatsappStatusQueryKey(orgId) }),
  });
}
